import { useRef, useEffect, useCallback } from 'react';
import { FireflySystem } from './FireflySystem';

interface FireflyCanvasProps {
  /** 是否启用滚动渐隐（仅首页 Hero 需要） */
  scrollFade?: boolean;
  /** 是否启用鼠标交互 */
  mouseInteraction?: boolean;
  /** 速度倍率 — 首页用 0.4（极慢），其他页面用 1.0 */
  speed?: number;
  /** 粒子数量倍率 — 首页充足(1.0)，其他页面少量(0.5) */
  density?: number;
}

export default function FireflyCanvas({
  scrollFade = false,
  mouseInteraction = true,
  speed = 1.0,
  density = 1.0,
}: FireflyCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const systemRef = useRef<FireflySystem | null>(null);
  const reducedMotionRef = useRef(false);
  const speedRef = useRef(speed);
  const densityRef = useRef(density);

  // 更新 ref 保持最新
  speedRef.current = speed;
  densityRef.current = density;

  useEffect(() => {
    if (mouseInteraction) {
      // 只有开启了鼠标交互才追踪
    }
  }, [mouseInteraction]);

  // 检测 reduced motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
      if (e.matches && systemRef.current) {
        systemRef.current.stop();
      } else if (!e.matches && systemRef.current) {
        systemRef.current.start();
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (reducedMotionRef.current) return;

    const system = new FireflySystem(canvas, densityRef.current);
    system.setSpeedMultiplier(speedRef.current);
    systemRef.current = system;
    system.start();

    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        if (systemRef.current) systemRef.current.resize();
      }, 100);
    };
    window.addEventListener('resize', handleResize);

    const handleVisibility = () => {
      if (!systemRef.current) return;
      if (document.hidden) systemRef.current.stop();
      else if (!reducedMotionRef.current) systemRef.current.start();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimeout(resizeTimeout);
      system.destroy();
      systemRef.current = null;
    };
  }, []);

  // 当 speed 或 density 动态变化时更新
  useEffect(() => {
    if (systemRef.current) {
      systemRef.current.setSpeedMultiplier(speed);
      systemRef.current.setDensityMultiplier(density);
    }
  }, [speed, density]);

  // 滚动进度
  useEffect(() => {
    if (!scrollFade || !systemRef.current) return;
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const progress = maxScroll > 0 ? scrollTop / maxScroll : 0;
        if (systemRef.current) systemRef.current.setScrollProgress(progress);
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollFade]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!mouseInteraction || !systemRef.current || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      systemRef.current.setMouse(e.clientX - rect.left, e.clientY - rect.top);
    },
    [mouseInteraction]
  );

  const handleMouseLeave = useCallback(() => {
    if (systemRef.current) systemRef.current.clearMouse();
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: mouseInteraction ? 'auto' : 'none',
        zIndex: 15,
      }}
    />
  );
}
